import { useEffect, useRef } from 'react';
import { isEffectivelyLocked, plainToRich, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import { isHttpUrl } from '@os/shared';
import type { EditorController } from '../editor/controller';
import { CLIP_MIME, clipboardText, parseClipPayload, pasteOffset, payloadBounds, rememberCopy, serializeSelection } from './clipboard';

const imagesOf = (list: FileList | null | undefined) => [...(list ?? [])].filter((f) => f.type.startsWith('image/') || f.type === '');
/** Fields and in-place editors keep the browser's own copy, cut and paste. */
const inField = (t: EventTarget | null) => t instanceof Element && !!t.closest('input, textarea, select, [contenteditable], [data-canvas-ignore]');

/**
 * Copy, cut and paste of board objects through the system clipboard, anywhere on the page while the board is open:
 * board objects first, then image files (P4 import), a URL (link card or embed), plain text (a text object).
 */
export function useClipboard(o: {
  store: SceneStore;
  controller: EditorController;
  importFiles: (files: File[], at: Point) => void;
  editing: () => boolean;
  /** False without `can_export` (P6 Ruling 15: copying objects out is an export); paste is unaffected. */
  canCopyOut?: boolean;
}): void {
  const ref = useRef(o);
  ref.current = o;
  useEffect(() => {
    const at = () => ref.current.controller.pointerWorld ?? ref.current.controller.viewCenter();
    const copy = (e: ClipboardEvent, cut: boolean) => {
      const { store, controller, editing, canCopyOut = true } = ref.current;
      const sel = controller.selection;
      if (!canCopyOut || editing() || inField(e.target) || sel.length === 0 || !e.clipboardData) return;
      e.preventDefault();
      const payload = serializeSelection(store, sel);
      rememberCopy(payload);
      e.clipboardData.setData(CLIP_MIME, JSON.stringify(payload));
      e.clipboardData.setData('text/plain', clipboardText(store, sel));
      if (cut && !store.readOnly) {
        const ids = sel.filter((id) => store.getObject(id) && !isEffectivelyLocked(store.graph(), (x) => store.getObject(x), id));
        store.commands.deleteObjects(ids);
        controller.setSelection(sel.filter((id) => !ids.includes(id)));
      }
    };
    const onCopy = (e: ClipboardEvent) => copy(e, false);
    const onCut = (e: ClipboardEvent) => copy(e, true);
    const onPaste = (e: ClipboardEvent) => {
      const { store, controller, importFiles, editing } = ref.current;
      if (editing() || inField(e.target) || store.readOnly || !e.clipboardData) return;
      const data = e.clipboardData;
      const payload = data.types?.includes(CLIP_MIME) || data.getData?.(CLIP_MIME) ? parseClipPayload(data.getData(CLIP_MIME)) : null;
      if (payload) {
        e.preventDefault();
        // On the grid when snapping is on (P16): the pasted group's top-left corner lands on a grid line.
        const b = payloadBounds(payload);
        const offset = b ? controller.snapOffset({ x: b.minX, y: b.minY }, pasteOffset(payload, at())) : pasteOffset(payload, at());
        const ids = store.commands.insertObjects(payload.objects, { offset });
        if (ids.length) controller.setSelection(ids.filter((id) => (store.getObject(id)?.parentId ?? null) === null || !ids.includes(store.getObject(id)!.parentId!)));
        return;
      }
      const files = imagesOf(data.files);
      if (files.length) {
        e.preventDefault();
        importFiles(files, at());
        return;
      }
      const text = (data.getData?.('text/plain') ?? '').trim();
      if (!text) return;
      e.preventDefault();
      if (isHttpUrl(text)) {
        controller.createLink(text, at());
        return;
      }
      const p = at();
      const id = store.commands.createObject({
        type: 'text', text, content: plainToRich(text), fontSize: 24, parentId: null,
        x: p.x - 120, y: p.y - 24, w: 240, h: Math.max(48, text.split('\n').length * 32), rotation: 0, locked: false, style: {},
      });
      if (id) controller.setSelection([id]);
    };
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCut);
      document.removeEventListener('paste', onPaste);
    };
  }, []);
}
